// App-wide field behaviour, applied automatically to every screen (and every window) as it appears:
//  • Dates: every <input type="date"> becomes one text box in dd/mm/yyyy with a calendar button. The original
//    input stays (hidden) with its name and ISO value (yyyy-mm-dd), so forms and change handlers work unchanged.
//  • Money: price / cost / amount boxes show 2 decimals with thousands separators (1,234.50) once you leave them,
//    while their value (what forms and screens read) stays the plain number.
//  • Number boxes: arrow keys and the mouse wheel never change the value.

import { orgToday } from "./lib.js";
import { formatMoney, isoToDmy, parseDmy, readDate } from "./fieldformat.js";
export { formatMoney, isoToDmy, parseDmy, readDate };

const pad = (n: number) => String(n).padStart(2, "0");
const todayIso = () => orgToday(); // the organisation's day
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// ---- Money --------------------------------------------------------------------------------------------
const MONEY_SELECTOR = [
  "input[data-money]", "input.po-price", "input.lc-amount", 'input[type="number"][name="price"]', 'input[type="number"][name="unitCost"]',
  'input[type="number"][name="makingCost"]', 'input[type="number"][name="otherCost"]', 'input[type="number"][name="targetCost"]',
  'input[type="number"][name="estimatedCost"]', 'input[type="number"][name$="PricePerUser"]',
].join(",");
const nativeValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!;
function enhanceMoney(el: HTMLInputElement): void {
  el.dataset.money = "1";
  el.dataset.falconMoney = "1";
  el.type = "text";
  el.inputMode = "decimal";
  el.autocomplete = "off";
  el.classList.add("falcon-money");
  // What code reads is the plain number ("1234.5"); what the box shows is "1,234.50".
  Object.defineProperty(el, "value", {
    configurable: true,
    get() { return String(nativeValue.get!.call(this)).replace(/,/g, "").trim(); },
    set(v: string) { nativeValue.set!.call(this, formatMoney(v)); },
  });
  nativeValue.set!.call(el, formatMoney(nativeValue.get!.call(el)));
  el.addEventListener("beforeinput", (e) => {
    const data = (e as InputEvent).data;
    if (!data) return;
    const now = String(nativeValue.get!.call(el));
    const kept = now.slice(0, el.selectionStart ?? now.length) + now.slice(el.selectionEnd ?? now.length);
    if (/[^0-9.,]/.test(data) || (data.includes(".") && kept.includes("."))) e.preventDefault();   // numbers and one decimal point only
  });
  el.addEventListener("blur", () => {
    const raw = String(nativeValue.get!.call(el)).replace(/,/g, "").trim();
    const shown = formatMoney(raw);
    if (raw !== "" && !shown) { el.setAttribute("aria-invalid", "true"); el.title = "Enter an amount, for example 1234.50"; return; }
    el.removeAttribute("aria-invalid");
    el.title = "";
    if (shown !== nativeValue.get!.call(el)) nativeValue.set!.call(el, shown);
  });
}

// ---- Dates --------------------------------------------------------------------------------------------
function enhanceDate(orig: HTMLInputElement): void {
  if (orig.dataset.falconDate) return;
  orig.dataset.falconDate = "1";
  const wrap = document.createElement("span");
  wrap.className = "falcon-date";
  const text = document.createElement("input");
  text.type = "text";
  text.className = `falcon-date-text ${orig.className}`.trim();
  text.placeholder = "dd/mm/yyyy";
  text.inputMode = "numeric";
  text.autocomplete = "off";
  text.maxLength = 10;
  text.value = isoToDmy(orig.value);
  for (const a of ["id", "aria-label", "title", "required"]) {
    const v = orig.getAttribute(a);
    if (v !== null) { text.setAttribute(a, v); if (a === "id") orig.removeAttribute("id"); }
  }
  if (!text.getAttribute("aria-label")) text.setAttribute("aria-label", `${orig.name || "Date"} (dd/mm/yyyy)`);
  const locked = orig.disabled || orig.readOnly;
  text.disabled = orig.disabled;
  text.readOnly = orig.readOnly;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "falcon-date-btn";
  btn.tabIndex = -1;   // Tab goes to the next field; the calendar opens with the button, Alt+↓ or F4
  btn.setAttribute("aria-label", "Open calendar");
  btn.title = "Calendar (Alt+↓)";
  btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v3M17 3v3M4 8h16M5 5h14v15H5z"/></svg>';
  btn.disabled = locked;
  const msg = document.createElement("small");
  msg.className = "falcon-date-msg";
  msg.hidden = true;
  const row = document.createElement("span");
  row.className = "falcon-date-row";
  orig.parentNode!.insertBefore(wrap, orig);
  row.append(text, btn);
  wrap.append(row, orig, msg);
  orig.type = "hidden";
  // Code that sets the original's value (defaults, "today" buttons) also updates what is shown.
  Object.defineProperty(orig, "value", {
    configurable: true,
    get() { return nativeValue.get!.call(this); },
    set(v: string) { nativeValue.set!.call(this, v); text.value = isoToDmy(v); showError(false); },
  });
  // ETC, ETD, ETA and due dates look ahead: a day and month typed without a year that would be more than 3 months
  // ago means next year (the box then says so).
  const future = orig.hasAttribute("data-future");
  const showError = (on: boolean) => {
    text.toggleAttribute("aria-invalid", on);
    wrap.classList.toggle("is-invalid", on);
    wrap.classList.remove("is-noted");
    msg.hidden = !on;
    msg.textContent = on ? "Enter a real date as dd/mm/yyyy, for example 24/09/2026." : "";
  };
  const showNote = (note: string) => {
    wrap.classList.add("is-noted");
    msg.hidden = false;
    msg.textContent = note;
  };
  const commit = (iso: string) => {
    const before = nativeValue.get!.call(orig);
    nativeValue.set!.call(orig, iso);
    text.value = isoToDmy(iso);
    showError(false);
    if (before !== iso) {
      orig.dispatchEvent(new Event("input", { bubbles: true }));
      orig.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };
  const fromText = () => {
    const typed = text.value.trim();
    const shownNow = isoToDmy(nativeValue.get!.call(orig));
    if (typed && typed === shownNow && !wrap.classList.contains("is-invalid")) return true;   // already read (change, then blur)
    const got = readDate(typed, { future, today: new Date(`${orgToday()}T12:00:00`) });
    if (got === null) { showError(true); return false; }
    commit(got.iso);
    if (got.nextYear) showNote(`Taken as next year: ${isoToDmy(got.iso)}`);
    return true;
  };
  text.addEventListener("beforeinput", (e) => {
    const data = (e as InputEvent).data;
    if (data && /[^0-9/.\-\s]/.test(data)) e.preventDefault();
  });
  text.addEventListener("change", (e) => { e.stopPropagation(); fromText(); });
  text.addEventListener("input", (e) => e.stopPropagation());
  text.addEventListener("blur", () => { fromText(); });
  text.addEventListener("keydown", (e) => {
    if ((e.altKey && e.key === "ArrowDown") || e.key === "F4") { e.preventDefault(); if (!locked) openPicker(text, orig, commit); }
    else if (e.key === "Enter") fromText();
  });
  btn.addEventListener("click", () => { if (!locked) openPicker(text, orig, commit); });
}

// One calendar pop-up for the whole app.
let pick: { el: HTMLDivElement; text: HTMLInputElement; commit: (iso: string) => void; month: Date; focus: string } | null = null;
function closePicker(refocus = true): void {
  if (!pick) return;
  const t = pick.text;
  pick.el.remove();
  pick = null;
  document.removeEventListener("mousedown", outside, true);
  if (refocus) t.focus();
}
function outside(e: MouseEvent): void {
  if (pick && !pick.el.contains(e.target as Node) && !(e.target as HTMLElement).closest?.(".falcon-date")) closePicker(false);
}
function openPicker(text: HTMLInputElement, orig: HTMLInputElement, commit: (iso: string) => void): void {
  closePicker(false);
  const typed = parseDmy(text.value, { future: orig.hasAttribute("data-future") });
  const current = typed || nativeValue.get!.call(orig) || todayIso();
  const el = document.createElement("div");
  el.className = "falcon-datepicker";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Choose a date");
  document.body.appendChild(el);
  pick = { el, text, commit, month: new Date(Number(current.slice(0, 4)), Number(current.slice(5, 7)) - 1, 1), focus: current };
  renderPicker();
  placePicker();
  document.addEventListener("mousedown", outside, true);
  el.querySelector<HTMLButtonElement>(".is-focus")?.focus();
}
/** Under the date box (or above it when there's no room below), kept there while the page scrolls. */
function placePicker(): void {
  if (!pick) return;
  const r = pick.text.getBoundingClientRect(), el = pick.el;
  const h = el.offsetHeight, w = el.offsetWidth;
  el.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
  el.style.top = `${r.bottom + 4 + h > window.innerHeight && r.top - h - 4 > 0 ? r.top - h - 4 : r.bottom + 4}px`;
}
window.addEventListener("scroll", placePicker, true);
window.addEventListener("resize", placePicker);
function renderPicker(): void {
  if (!pick) return;
  const { el, month, focus } = pick;
  const y = month.getFullYear(), m = month.getMonth();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;   // Monday first
  const days = new Date(y, m + 1, 0).getDate();
  const selected = parseDmy(pick.text.value) || "";
  const today = todayIso();
  let cells = "";
  for (let i = 0; i < lead; i++) cells += "<span></span>";
  for (let d = 1; d <= days; d++) {
    const iso = `${y}-${pad(m + 1)}-${pad(d)}`;
    cells += `<button type="button" data-iso="${iso}" class="${iso === today ? "is-today " : ""}${iso === selected ? "is-selected " : ""}${iso === focus ? "is-focus" : ""}" tabindex="${iso === focus ? 0 : -1}" aria-label="${d} ${MONTHS[m]} ${y}${iso === today ? " (today)" : ""}">${d}</button>`;
  }
  el.innerHTML = `<div class="falcon-dp-head">
  <button type="button" data-nav="-12" aria-label="Previous year" title="Previous year">«</button><button type="button" data-nav="-1" aria-label="Previous month" title="Previous month">‹</button>
  <strong>${MONTHS[m]} ${y}</strong>
  <button type="button" data-nav="1" aria-label="Next month" title="Next month">›</button><button type="button" data-nav="12" aria-label="Next year" title="Next year">»</button></div>
<div class="falcon-dp-week"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
<div class="falcon-dp-grid">${cells}</div>
<div class="falcon-dp-foot"><button type="button" data-iso="${today}">Today</button><button type="button" data-clear>Clear</button></div>`;
}
document.addEventListener("click", (e) => {
  if (!pick) return;
  const b = (e.target as HTMLElement).closest?.("button");
  if (!b || !pick.el.contains(b)) return;
  if (b.dataset.nav) {
    pick.month = new Date(pick.month.getFullYear(), pick.month.getMonth() + Number(b.dataset.nav), 1);
    const f = new Date(pick.focus);
    const day = Math.min(f.getDate() || 1, new Date(pick.month.getFullYear(), pick.month.getMonth() + 1, 0).getDate());
    pick.focus = `${pick.month.getFullYear()}-${pad(pick.month.getMonth() + 1)}-${pad(day)}`;
    renderPicker();
    pick.el.querySelector<HTMLButtonElement>(`[data-nav="${b.dataset.nav}"]`)?.focus();
  } else if (b.dataset.iso) {
    const c = pick.commit;
    closePicker();
    c(b.dataset.iso);
  } else if (b.hasAttribute("data-clear")) {
    const c = pick.commit;
    closePicker();
    c("");
  }
});
document.addEventListener("keydown", (e) => {
  if (!pick) return;
  if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closePicker(); return; }
  if (!pick.el.contains(document.activeElement)) return;
  const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
  if (moves[e.key] !== undefined || e.key === "PageUp" || e.key === "PageDown") {
    if (!(document.activeElement as HTMLElement).dataset.iso) return;
    e.preventDefault();
    const f = new Date(`${pick.focus}T12:00:00`);
    if (moves[e.key] !== undefined) f.setDate(f.getDate() + moves[e.key]);
    else f.setMonth(f.getMonth() + (e.key === "PageUp" ? -1 : 1));
    pick.focus = `${f.getFullYear()}-${pad(f.getMonth() + 1)}-${pad(f.getDate())}`;
    pick.month = new Date(f.getFullYear(), f.getMonth(), 1);
    renderPicker();
    pick.el.querySelector<HTMLButtonElement>(".is-focus")?.focus();
  } else if (e.key === "Tab") {
    closePicker();
  }
}, true);

// A form with a date that isn't real isn't sent: the field says what's wrong.
document.addEventListener("submit", (e) => {
  const bad = (e.target as HTMLElement).querySelector<HTMLInputElement>(".falcon-date-text[aria-invalid]");
  if (bad) { e.preventDefault(); e.stopImmediatePropagation(); bad.focus(); }
}, true);

// ---- Number boxes: arrows and the mouse wheel never change the value -------------------------------------
document.addEventListener("keydown", (e) => {
  const t = e.target as HTMLInputElement;
  if (t?.tagName === "INPUT" && t.type === "number" && (e.key === "ArrowUp" || e.key === "ArrowDown")) e.preventDefault();
}, true);
document.addEventListener("wheel", (e) => {
  const t = e.target as HTMLInputElement;
  if (t?.tagName === "INPUT" && t.type === "number" && document.activeElement === t) e.preventDefault();
}, { passive: false, capture: true });

// ---- Apply to everything, now and as screens change ------------------------------------------------------------
export function enhanceFields(root: ParentNode = document): void {
  for (const el of root.querySelectorAll<HTMLInputElement>('input[type="date"]:not([data-falcon-date])')) enhanceDate(el);
  for (const el of root.querySelectorAll<HTMLInputElement>(MONEY_SELECTOR)) if (!el.dataset.falconMoney) enhanceMoney(el);
}
export function startFieldEnhancer(): void {
  enhanceFields();
  new MutationObserver((list) => {
    for (const m of list) for (const n of m.addedNodes) if (n.nodeType === 1) enhanceFields(n as Element);
  }).observe(document.body, { childList: true, subtree: true });
}
